import { useEffect, useState, useCallback, useMemo } from "react";
import { toast } from "sonner";
import { Sparkles, Check, X, Clock, User, Calendar, MessageSquare, Trash2, Send } from "lucide-react";
import { format, parseISO } from "date-fns";
import { pt } from "date-fns/locale";
import { api, formatApiError, BACKEND_URL } from "../../lib/api";
import useBulkSelect from "../../lib/useBulkSelect";
import BulkActionBar, { BulkCheckbox } from "../../components/BulkActionBar";

const EXTRA_LABELS = {
  lavandaria: "Lavandaria",
  consumiveis: "Consumíveis",
  limpeza_profunda: "Limpeza profunda",
  limpeza_exterior: "Limpeza exterior",
  relva: "Corte de relva",
  manutencao: "Manutenção",
};

const STATUS_STYLES = {
  pending:          { color: "text-[#f59e0b]", border: "border-[#f59e0b]/40", bg: "bg-[#f59e0b]/5", label: "Pendente", icon: Clock },
  pending_client:   { color: "text-[#d4af37]", border: "border-[#d4af37]/40", bg: "bg-[#d4af37]/5", label: "Aguarda cliente", icon: Send },
  client_approved:  { color: "text-[#10b981]", border: "border-[#10b981]/40", bg: "bg-[#10b981]/5", label: "Cliente aprovou", icon: Check },
  client_rejected:  { color: "text-[#fca5a5]", border: "border-[#ef4444]/40", bg: "bg-[#ef4444]/5", label: "Cliente recusou", icon: X },
  approved:         { color: "text-[#10b981]", border: "border-[#10b981]/40", bg: "bg-[#10b981]/5", label: "Aprovado", icon: Check },
  rejected:         { color: "text-[#fca5a5]", border: "border-[#fca5a5]/40", bg: "bg-[#fca5a5]/5", label: "Rejeitado", icon: X },
};

const FILTERS = [
  { key: "pending",         label: "Pendentes" },
  { key: "pending_client",  label: "Aguarda cliente" },
  { key: "client_approved", label: "Aprovadas cliente" },
  { key: "client_rejected", label: "Recusadas cliente" },
  { key: "approved",        label: "Aprovadas" },
  { key: "rejected",        label: "Rejeitadas" },
  { key: "all",             label: "Todas" },
];

/**
 * AdminExtrasSuggestions — admin decides on extras that employees suggested
 * during cleanings. Approving auto-adds the extra to the booking.
 *
 * Admin may optionally add a note with the decision (e.g. explaining why
 * a suggestion was rejected, or confirming the extra fee will be billed
 * separately).
 */
export default function AdminExtrasSuggestions() {
  const [filter, setFilter] = useState("pending");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deciding, setDeciding] = useState(null); // {id, decision}
  const [note, setNote] = useState("");

  const load = useCallback(async () => {
    try {
      const params = filter === "all" ? {} : { status: filter };
      const { data } = await api.get("/admin/extra-suggestions", { params });
      setData(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  async function submitDecision() {
    if (!deciding) return;
    try {
      await api.patch(`/admin/extra-suggestions/${deciding.id}`, {
        decision: deciding.decision,
        admin_note: note || null,
      });
      toast.success(deciding.decision === "approved"
        ? "Aprovado. Extra adicionado à reserva."
        : "Sugestão rejeitada.");
      setDeciding(null); setNote("");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  }

  async function sendToClient(id, value) {
    const n = parseFloat(value);
    if (!Number.isFinite(n) || n <= 0) { toast.error("Define um valor maior que 0."); return; }
    try {
      await api.patch(`/admin/extra-suggestions/${id}/review`, {
        admin_value: n,
        send_to_client: true,
      });
      toast.success("Enviado ao cliente para aprovação.");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  }

  async function onBehalf(id, decision) {
    const label = decision === "approve" ? "aprovar" : "recusar";
    if (!window.confirm(`Confirmas que o cliente quis ${label} por telefone?`)) return;
    try {
      await api.post(`/admin/approvals/extra/${id}/on-behalf`, { decision });
      toast.success(decision === "approve" ? "Aprovado em nome do cliente." : "Recusado em nome do cliente.");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  }

  async function deleteSuggestion(id) {
    if (!window.confirm("Eliminar esta sugestão? Esta ação é irreversível.")) return;
    try {
      await api.delete(`/admin/extra-suggestions/${id}`);
      toast.success("Sugestão eliminada");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  }

  const items = data?.items || [];
  const pendingCount = data?.pending_count || 0;

  const ids = useMemo(() => items.map(it => it.id), [items]);
  const bulk = useBulkSelect(ids);
  async function bulkRun(selected, fn, { successMsg }) {
    const res = await Promise.allSettled(selected.map(fn));
    const ok = res.filter(r => r.status === "fulfilled").length;
    const ko = res.length - ok;
    if (ok > 0) toast.success(`${ok} ${successMsg}${ko > 0 ? ` · ${ko} falharam` : ""}`);
    else toast.error("Nenhuma operação concluída");
    load();
  }

  return (
    <div className="space-y-8">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="eyebrow mb-3">Oportunidades</div>
          <h1 className="font-display text-5xl">Extras sugeridos.</h1>
          <div className="text-sm text-[#a0b2af] mt-2 max-w-2xl">
            Extras que os funcionários recomendaram após inspeccionar cada alojamento. Aprovar adiciona o extra automaticamente à reserva para faturação.
          </div>
        </div>
        {pendingCount > 0 && (
          <div className="px-4 py-2 border border-[#d4af37] text-[#d4af37] bg-[#d4af37]/5 rounded-sm animate-pulse text-xs uppercase tracking-widest"
               data-testid="pending-extras-badge">
            {pendingCount} {pendingCount === 1 ? "sugestão pendente" : "sugestões pendentes"}
          </div>
        )}
      </div>

      {/* Filter tabs */}
      <div className="flex flex-wrap gap-2" data-testid="extras-filters">
        {FILTERS.map(f => (
          <button key={f.key} onClick={() => setFilter(f.key)}
                  className={`px-4 py-2 text-xs uppercase tracking-widest border rounded-sm ${filter === f.key ? "border-[#d4af37] text-[#d4af37] bg-[#062c27]" : "border-[#1a4640] text-[#a0b2af]"}`}
                  data-testid={`extra-filter-${f.key}`}>{f.label}</button>
        ))}
      </div>

      {loading && <div className="text-[#a0b2af] text-sm">A carregar…</div>}

      {!loading && items.length === 0 && (
        <div className="lux-card p-10 text-center text-sm text-[#a0b2af]" data-testid="extras-empty">
          {filter === "pending" ? "✓ Zero sugestões pendentes. Tudo em dia." : "Sem sugestões neste estado."}
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-3">
        {items.length > 0 && (
          <label className="col-span-full inline-flex items-center gap-2 px-3 py-2 text-[10px] uppercase tracking-widest text-[#a0b2af] border border-[#1a4640] rounded-sm w-fit cursor-pointer hover:text-[#d4af37] hover:border-[#d4af37]/50">
            <BulkCheckbox
              checked={bulk.allSelected}
              indeterminate={bulk.someSelected}
              onChange={() => bulk.toggleAll(ids)}
              label="Selecionar todos"
            />
            Selecionar todos
          </label>
        )}
        {items.map(it => (
          <div key={it.id} className="relative">
            <div className="absolute top-5 right-5 z-10">
              <BulkCheckbox
                id={it.id}
                checked={bulk.isSelected(it.id)}
                onChange={() => bulk.toggle(it.id)}
                label="Selecionar sugestão"
              />
            </div>
            <ExtraCard it={it}
                       onApprove={() => { setDeciding({ id: it.id, decision: "approved" }); setNote(""); }}
                       onReject={() => { setDeciding({ id: it.id, decision: "rejected" }); setNote(""); }}
                       onDelete={() => deleteSuggestion(it.id)}
                       onSend={sendToClient}
                       onBehalf={onBehalf} />
          </div>
        ))}
      </div>

      <BulkActionBar
        count={bulk.count}
        ids={Array.from(bulk.selected)}
        onClear={bulk.clear}
        actions={[
          {
            key: "approve", label: "Aprovar", icon: Check, variant: "gold",
            confirm: "Aprovar {count} sugestão(ões)? Os extras serão adicionados automaticamente.",
            onClick: (sel) => bulkRun(sel, (id) => api.patch(`/admin/extra-suggestions/${id}`, { decision: "approved" }), { successMsg: "aprovada(s)" }),
          },
          {
            key: "reject", label: "Rejeitar", icon: X, variant: "default",
            confirm: "Rejeitar {count} sugestão(ões)?",
            onClick: (sel) => bulkRun(sel, (id) => api.patch(`/admin/extra-suggestions/${id}`, { decision: "rejected" }), { successMsg: "rejeitada(s)" }),
          },
          {
            key: "delete", label: "Eliminar", icon: Trash2, variant: "danger",
            confirm: "Eliminar {count} sugestão(ões)? Ação irreversível.",
            onClick: (sel) => bulkRun(sel, (id) => api.delete(`/admin/extra-suggestions/${id}`), { successMsg: "eliminada(s)" }),
          },
        ]}
      />

      {/* Decision modal */}
      {deciding && (
        <div className="fixed inset-0 bg-black/70 z-40 grid place-items-center p-4" onClick={() => setDeciding(null)}>
          <div onClick={e => e.stopPropagation()}
               className="bg-[#062c27] border border-[#1a4640] max-w-lg w-full p-6 space-y-4"
               data-testid="decision-modal">
            <div>
              <div className="eyebrow">
                {deciding.decision === "approved" ? "Aprovar sugestão" : "Rejeitar sugestão"}
              </div>
              <h2 className="font-display text-2xl mt-1">
                {deciding.decision === "approved"
                  ? "O extra será adicionado automaticamente à reserva."
                  : "O funcionário será notificado da rejeição."}
              </h2>
            </div>
            <div className="space-y-2">
              <label className="text-xs uppercase tracking-widest text-[#a0b2af]">
                Nota para o funcionário (opcional)
              </label>
              <textarea rows={3} value={note} onChange={e => setNote(e.target.value)}
                        placeholder={deciding.decision === "approved"
                          ? "Ex: Muito bom. Cobrar como extra na fatura final."
                          : "Ex: Não faz parte do contrato com este cliente."}
                        className="w-full bg-[#041f1b] border border-[#1a4640] rounded-sm px-4 py-3 text-sm"
                        data-testid="decision-note" />
            </div>
            <div className="flex gap-2 pt-2">
              <button onClick={submitDecision}
                      className={deciding.decision === "approved"
                        ? "btn-gold px-6 py-3 text-sm"
                        : "px-6 py-3 text-sm border border-[#fca5a5]/40 text-[#fca5a5] hover:bg-[#fca5a5]/10"}
                      data-testid="confirm-decision">
                Confirmar {deciding.decision === "approved" ? "aprovação" : "rejeição"}
              </button>
              <button onClick={() => setDeciding(null)}
                      className="px-6 py-3 text-sm text-[#a0b2af]">Cancelar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


function ExtraCard({ it, onApprove, onReject, onDelete, onSend, onBehalf }) {
  const st = STATUS_STYLES[it.status] || STATUS_STYLES.pending;
  const Ic = st.icon;
  const [adminValue, setAdminValue] = useState(it.admin_value ?? "");
  const photos = it.photos || [];
  return (
    <div className={`lux-card p-5 border-l-4 ${st.border}`} data-testid={`admin-extra-${it.id}`}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <span className={`text-xs uppercase tracking-widest px-2 py-0.5 border ${st.border} ${st.color} ${st.bg} inline-flex items-center gap-1`}>
            <Ic size={11} /> {st.label}
          </span>
          <div className="text-xs text-[#d4af37] uppercase tracking-widest mt-2">
            {EXTRA_LABELS[it.extra_id] || it.extra_id}
            {typeof it.admin_value === "number" && it.admin_value > 0 && (
              <span className="ml-2">· {Number(it.admin_value).toFixed(2)} €</span>
            )}
          </div>
        </div>
      </div>

      {it.note && (
        <div className="text-sm italic text-[#a0b2af] mb-3 p-3 bg-[#0a3a33] border-l-2 border-[#d4af37]/50">
          <MessageSquare size={12} className="inline mr-1 text-[#d4af37]" />
          "{it.note}"
        </div>
      )}
      {it.admin_note && (
        <div className="text-xs text-[#fdfbf7] mb-3 p-2 bg-[#062c27] border border-[#1a4640]">
          <span className="text-[#d4af37] uppercase tracking-widest text-[9px]">Sua nota: </span>
          {it.admin_note}
        </div>
      )}
      {it.client_note && (
        <div className="text-xs text-[#fdfbf7] mb-3 p-2 bg-[#062c27] border border-[#1a4640]">
          <span className="text-[#d4af37] uppercase tracking-widest text-[9px]">Nota cliente: </span>
          {it.client_note}
        </div>
      )}

      <div className="flex flex-wrap gap-3 text-[11px] text-[#a0b2af] mb-3">
        <span className="inline-flex items-center gap-1">
          <User size={11} className="text-[#d4af37]" /> {it.employee_name}
        </span>
        <span className="inline-flex items-center gap-1">
          <Calendar size={11} className="text-[#d4af37]" />
          {format(parseISO(it.created_at), "d 'de' MMM 'às' HH:mm", { locale: pt })}
        </span>
      </div>

      {photos.length > 0 && (
        <div className="mb-3 flex flex-wrap gap-2">
          {photos.map(p => (
            <a key={p.id} href={`${BACKEND_URL}/api/files/${p.storage_path}`}
               target="_blank" rel="noreferrer"
               className="w-14 h-14 border border-[#1a4640] rounded-sm overflow-hidden hover:border-[#d4af37] transition-colors">
              <img src={`${BACKEND_URL}/api/files/${p.storage_path}`}
                   alt="foto extra" className="w-full h-full object-cover" />
            </a>
          ))}
        </div>
      )}

      {it.status === "pending" && (
        <div className="pt-3 border-t border-[#1a4640] space-y-3">
          <div className="flex gap-2 items-end flex-wrap">
            <label className="text-xs text-[#a0b2af] flex-1 min-w-[120px]">
              <span className="uppercase tracking-widest">Valor (€)</span>
              <input type="number" step="0.01" min="0" value={adminValue}
                     onChange={e => setAdminValue(e.target.value)}
                     placeholder="0.00"
                     className="mt-1 w-full bg-[#062c27] border border-[#1a4640] rounded-sm px-3 py-2 text-sm text-[#fdfbf7]"
                     data-testid={`extra-value-${it.id}`} />
            </label>
            <button onClick={() => onSend(it.id, adminValue)}
                    className="btn-gold px-4 py-2 text-xs uppercase tracking-widest inline-flex items-center gap-2"
                    data-testid={`extra-send-${it.id}`}>
              <Send size={12} /> Enviar ao cliente
            </button>
          </div>
          <div className="flex gap-2 flex-wrap">
            <button onClick={onApprove}
                    className="px-3 py-1.5 text-xs text-[#10b981] border border-[#10b981]/40 hover:bg-[#10b981]/10 inline-flex items-center gap-1"
                    data-testid={`approve-${it.id}`}>
              <Check size={12} /> Aprovar direto
            </button>
            <button onClick={onReject}
                    className="px-3 py-1.5 text-xs border border-[#fca5a5]/40 text-[#fca5a5] hover:bg-[#fca5a5]/10 inline-flex items-center gap-1"
                    data-testid={`reject-${it.id}`}>
              <X size={12} /> Rejeitar
            </button>
            <button onClick={onDelete}
                    className="px-3 py-1.5 text-xs border border-[#ef4444]/60 text-[#fca5a5] hover:bg-[#ef4444]/10 transition-colors rounded-sm inline-flex items-center gap-1 ml-auto"
                    data-testid={`delete-suggestion-${it.id}`}>
              <Trash2 size={12} /> Eliminar
            </button>
          </div>
        </div>
      )}
      {it.status !== "pending" && (
        <div className="flex gap-2 pt-3 border-t border-[#1a4640] flex-wrap">
          {it.status === "pending_client" && (
            <>
              <button onClick={() => onBehalf(it.id, "approve")}
                      className="px-3 py-1.5 text-xs text-[#10b981] border border-[#10b981]/40 hover:bg-[#10b981]/10 inline-flex items-center gap-1"
                      data-testid={`extra-on-behalf-approve-${it.id}`}
                      title="Cliente aprovou por telefone">
                <Check size={12} /> Aprovar (tel.)
              </button>
              <button onClick={() => onBehalf(it.id, "reject")}
                      className="px-3 py-1.5 text-xs text-[#fca5a5] border border-[#fca5a5]/40 hover:bg-[#fca5a5]/10 inline-flex items-center gap-1"
                      data-testid={`extra-on-behalf-reject-${it.id}`}
                      title="Cliente recusou por telefone">
                <X size={12} /> Recusar (tel.)
              </button>
            </>
          )}
          <button onClick={onDelete}
                  className="px-3 py-1.5 text-xs text-[#fca5a5] border border-[#ef4444]/60 hover:bg-[#ef4444]/10 transition-colors rounded-sm inline-flex items-center gap-1 ml-auto"
                  data-testid={`delete-suggestion-${it.id}`}>
            <Trash2 size={12} /> Eliminar registo
          </button>
        </div>
      )}
    </div>
  );
}
